import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, CreditCard, QrCode, Save, Check, X, Eye, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const AdminBankDetailsPage = () => {
  const [bankDetails, setBankDetails] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewPayment, setPreviewPayment] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');

  useEffect(() => {
    loadData();
  }, [filterStatus]);

  const loadData = async () => {
    try {
      const [bankRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/bank-details`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/payments?status=${filterStatus}`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      ]);
      setBankDetails(bankRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBankDetails(prev => ({ ...prev, qr_code_url: response.data.url }));
      toast.success('QR Code uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload QR code');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/bank-details`, bankDetails, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Bank details saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (paymentId) => {
    try {
      await axios.put(`${API}/payments/${paymentId}/approve`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Payment approved!');
      loadData();
      setPreviewPayment(null);
    } catch (error) {
      toast.error('Failed to approve payment');
    }
  };

  const handleReject = async (paymentId, reason = '') => {
    try {
      await axios.put(`${API}/payments/${paymentId}/reject?reason=${encodeURIComponent(reason)}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Payment rejected');
      loadData();
      setPreviewPayment(null);
    } catch (error) {
      toast.error('Failed to reject payment');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-12 w-12 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-details-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Bank Details & Payments</h2>
          <p className="text-muted-foreground">Manage bank account and review member payments</p>
        </div>
      </div>
      
      <Tabs defaultValue="bank" className="space-y-6">
        <TabsList>
          <TabsTrigger value="bank" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Bank Details
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payments
            {payments.filter(p => p.status === 'pending').length > 0 && (
              <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">
                {payments.filter(p => p.status === 'pending').length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
        
        {/* Bank Details Tab */}
        <TabsContent value="bank">
          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Account Details Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" style={{ color: '#D4AF37' }} />
                    Account Details
                  </CardTitle>
                  <CardDescription>Bank account information for payments</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Account Name</Label>
                    <Input 
                      value={bankDetails?.account_name || ''} 
                      onChange={(e) => setBankDetails({...bankDetails, account_name: e.target.value})} 
                      placeholder="Account holder name"
                      data-testid="account-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Account Number</Label>
                    <Input 
                      value={bankDetails?.account_number || ''} 
                      onChange={(e) => setBankDetails({...bankDetails, account_number: e.target.value})} 
                      placeholder="Account number"
                      data-testid="account-number-input"
                    />
                  </div>
                  
                  <div>
                    <Label>IFSC Code</Label>
                    <Input 
                      value={bankDetails?.ifsc_code || ''} 
                      onChange={(e) => setBankDetails({...bankDetails, ifsc_code: e.target.value})} 
                      placeholder="IFSC code"
                      data-testid="ifsc-code-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Bank Name</Label>
                    <Input 
                      value={bankDetails?.bank_name || ''} 
                      onChange={(e) => setBankDetails({...bankDetails, bank_name: e.target.value})} 
                      placeholder="Bank name"
                      data-testid="bank-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Branch</Label>
                    <Input 
                      value={bankDetails?.branch_name || ''} 
                      onChange={(e) => setBankDetails({...bankDetails, branch_name: e.target.value})} 
                      placeholder="Branch name"
                      data-testid="branch-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label>UPI ID (Optional)</Label>
                    <Input 
                      value={bankDetails?.upi_id || ''} 
                      onChange={(e) => setBankDetails({...bankDetails, upi_id: e.target.value})} 
                      placeholder="UPI ID"
                      data-testid="upi-id-input"
                    />
                  </div>
                </CardContent>
              </Card>
              
              {/* QR Code Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5" style={{ color: '#D4AF37' }} />
                    Payment QR Code
                  </CardTitle>
                  <CardDescription>QR code for quick payments</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-center">
                    <div className="w-64 h-64 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden" style={{ borderColor: '#E2E8F0' }}>
                      {bankDetails?.qr_code_url ? (
                        <img 
                          src={getImageUrl(bankDetails.qr_code_url)}
                          alt="Payment QR Code" 
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <QrCode className="w-24 h-24 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <Label>Upload New QR Code</Label>
                    <Input 
                      type="file" 
                      accept="image/*"
                      onChange={handleQRUpload}
                      disabled={uploading}
                      className="cursor-pointer"
                    />
                  </div>
                  
                  <div>
                    <Label>Or Enter QR Code URL</Label>
                    <Input 
                      value={bankDetails?.qr_code_url || ''} 
                      onChange={(e) => setBankDetails({...bankDetails, qr_code_url: e.target.value})} 
                      placeholder="https://example.com/qr-code.png"
                      data-testid="qr-url-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={saving || uploading}
                style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                data-testid="save-bank-details-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Bank Details
                  </>
                )}
              </Button>
            </div>
          </form>
        </TabsContent>
        
        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <CardTitle>Payment Submissions</CardTitle>
                  <CardDescription>Review and approve member payment screenshots</CardDescription>
                </div>
                <div className="flex gap-2">
                  {['pending', 'approved', 'rejected'].map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={filterStatus === status ? 'default' : 'outline'}
                      onClick={() => setFilterStatus(status)}
                      style={filterStatus === status ? { backgroundColor: '#D4AF37', color: '#0F172A' } : {}}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No {filterStatus} payments</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Purpose</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.payment_id}>
                          <TableCell className="font-medium">{payment.member_name}</TableCell>
                          <TableCell>₹{payment.amount.toLocaleString()}</TableCell>
                          <TableCell className="max-w-[150px] truncate">{payment.purpose}</TableCell>
                          <TableCell>{formatDate(payment.payment_date)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              payment.status === 'approved' ? 'bg-green-100 text-green-800' : 
                              payment.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setPreviewPayment(payment)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {payment.status === 'pending' && (
                                <>
                                  <Button 
                                    size="sm" 
                                    className="h-8 w-8 p-0"
                                    style={{ backgroundColor: '#22C55E' }}
                                    onClick={() => handleApprove(payment.payment_id)}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    className="h-8 w-8 p-0"
                                    onClick={() => handleReject(payment.payment_id)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Payment Preview Dialog */}
      <Dialog open={!!previewPayment} onOpenChange={() => setPreviewPayment(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
          </DialogHeader>
          {previewPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Member</Label>
                  <p className="font-semibold">{previewPayment.member_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Amount</Label>
                  <p className="font-semibold text-lg" style={{ color: '#D4AF37' }}>₹{previewPayment.amount.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Purpose</Label>
                  <p>{previewPayment.purpose}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Payment Date</Label>
                  <p>{formatDate(previewPayment.payment_date)}</p>
                </div>
                {previewPayment.transaction_id && (
                  <div>
                    <Label className="text-muted-foreground">Transaction ID</Label>
                    <p className="font-mono">{previewPayment.transaction_id}</p>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    previewPayment.status === 'approved' ? 'bg-green-100 text-green-800' : 
                    previewPayment.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {previewPayment.status.charAt(0).toUpperCase() + previewPayment.status.slice(1)}
                  </span>
                </div>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Payment Screenshot</Label>
                <div className="mt-2 rounded-lg border overflow-hidden">
                  <img 
                    src={getImageUrl(previewPayment.screenshot_url)}
                    alt="Payment Screenshot" 
                    className="w-full max-h-96 object-contain bg-gray-50"
                  />
                </div>
              </div>
              
              {previewPayment.status === 'pending' && (
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPreviewPayment(null)}>Cancel</Button>
                  <Button 
                    variant="destructive"
                    onClick={() => handleReject(previewPayment.payment_id)}
                  >
                    <X className="w-4 h-4 mr-2" /> Reject
                  </Button>
                  <Button 
                    style={{ backgroundColor: '#22C55E' }}
                    onClick={() => handleApprove(previewPayment.payment_id)}
                  >
                    <Check className="w-4 h-4 mr-2" /> Approve
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBankDetailsPage;
